package features.attendance.ui.modals

import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import shared.ui.ModalManager
import kotlinx.browser.document
import kotlinx.browser.window

object LocationPreviewModal {
    fun show(location: String) {
        console.log("📍 LocationPreviewModal.show() called with location:", location)
        
        // Parse coordinates from location string
        // Format: "Location ($lat, $lng)" or just coordinates
        val coordinates = parseCoordinates(location)
        
        if (coordinates == null) {
            window.alert("Invalid location format: $location")
            return
        }
        
        val (lat, lng) = coordinates
        console.log("📍 Parsed coordinates: lat=$lat, lng=$lng")
        
        // Create modal HTML
        val modalHtml = """
            <div id="location-preview-modal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div class="bg-dark-800 rounded-2xl border border-dark-700 max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                    <!-- Header -->
                    <div class="flex justify-between items-center p-6 border-b border-dark-700">
                        <h3 class="text-xl font-bold text-white">📍 Location Preview</h3>
                        <button id="close-location-modal" class="text-slate-400 hover:text-white transition p-2">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    
                    <!-- Map Container -->
                    <div id="map-container" class="w-full h-96 bg-dark-900"></div>
                    
                    <!-- Coordinates Info -->
                    <div class="p-6 border-t border-dark-700 bg-dark-900/50">
                        <div class="mb-4">
                            <p class="text-slate-400 text-sm mb-1">Coordinates</p>
                            <p class="text-white font-mono font-medium text-sm">$lat, $lng</p>
                        </div>
                        <div>
                            <a href="https://www.google.com/maps?q=$lat,$lng" target="_blank" 
                               class="inline-flex items-center gap-2 text-brand-cyan hover:text-brand-green transition">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                                </svg>
                                Open in Google Maps
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        """.trimIndent()
        
        // Insert modal into DOM
        val root = document.getElementById("root")
        val modalDiv = document.createElement("div")
        modalDiv.innerHTML = modalHtml
        root?.appendChild(modalDiv)
        
        // Initialize map after DOM is ready
        window.setTimeout({
            initializeMap(lat, lng)
            
            // Add close button handler
            document.getElementById("close-location-modal")?.addEventListener("click", {
                close()
            })
            
            // Close on backdrop click
            document.getElementById("location-preview-modal")?.addEventListener("click", { event ->
                if (event.target == document.getElementById("location-preview-modal")) {
                    close()
                }
            })
        }, 100)
    }
    
    private fun parseCoordinates(location: String): Pair<Double, Double>? {
        return try {
            // Try to extract coordinates from "Location ($lat, $lng)" format
            val regex = Regex("""\$(-?\d+\.?\d*),\s*\$(-?\d+\.?\d*)""")
            val match = regex.find(location)
            
            if (match != null) {
                val lat = match.groupValues[1].toDouble()
                val lng = match.groupValues[2].toDouble()
                Pair(lat, lng)
            } else {
                // Try direct coordinate format: "lat, lng"
                val parts = location.split(",").map { it.trim() }
                if (parts.size == 2) {
                    val lat = parts[0].toDouble()
                    val lng = parts[1].toDouble()
                    Pair(lat, lng)
                } else {
                    null
                }
            }
        } catch (e: Exception) {
            console.error("Failed to parse coordinates:", e)
            null
        }
    }
    
    private fun initializeMap(lat: Double, lng: Double) {
        console.log("🗺️ Initializing map with coordinates: $lat, $lng")
        
        // Initialize Leaflet map using dynamic JS
        val mapCode = """
            const mapContainer = document.getElementById('map-container');
            if (mapContainer && typeof L !== 'undefined') {
                const map = L.map('map-container').setView([$lat, $lng], 15);
                
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '© OpenStreetMap contributors',
                    maxZoom: 19
                }).addTo(map);
                
                const marker = L.marker([$lat, $lng]).addTo(map);
                marker.bindPopup('<b>Clock In Location</b><br>Lat: $lat<br>Lng: $lng').openPopup();
                
                setTimeout(() => map.invalidateSize(), 100);
                
                console.log('✅ Map initialized successfully');
            } else {
                console.error('❌ Map container or Leaflet not found');
            }
        """
        
        js("eval(mapCode)")
    }
    
    fun close() {
        console.log("📍 Closing location preview modal")
        val modal = document.getElementById("location-preview-modal")
        modal?.parentElement?.remove()
    }
}
